import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocialMediaPost, SocialPostStatus } from '@/lib/api';
import { ArrowUp, ArrowDown, Paperclip, AlertTriangle } from 'lucide-react';
import { TwitterIcon, InstagramIcon, LinkedinIcon, FacebookIcon } from './PlatformIcons';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLATFORM_LABELS: Record<string, React.ReactNode> = {
  twitter: <TwitterIcon className="h-3.5 w-3.5" />,
  linkedin: <LinkedinIcon className="h-3.5 w-3.5" />,
  facebook: <FacebookIcon className="h-3.5 w-3.5" />,
  instagram: <InstagramIcon className="h-3.5 w-3.5" />,
};

const STATUS_CLASSES: Record<SocialPostStatus, string> = {
  draft: 'bg-[#F4F4F4] text-[#999999]',
  scheduled: 'bg-[#EBF3FF] text-[#2563EB]',
  publishing: 'bg-[#FEF9EC] text-[#B45309]',
  published: 'bg-[#EDFAF3] text-[#27A865]',
  failed: 'bg-[#FEF2F2] text-[#DC2626]',
  cancelled: 'bg-[#F4F4F4] text-[#6B7280]',
};

const STATUS_LABELS: Record<SocialPostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

type SortDir = 'asc' | 'desc';

interface PostsTableProps {
  posts: SocialMediaPost[];
  deletePost?: (postId: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Pill — low-saturation badge matching cannabis app
// ---------------------------------------------------------------------------
function Pill({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-[12px] font-[500] leading-tight whitespace-nowrap',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// PostsTable
// ---------------------------------------------------------------------------
export function PostsTable({ posts }: PostsTableProps) {
  const navigate = useNavigate();
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = [...posts].sort((a, b) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return sortDir === 'asc' ? diff : -diff;
  });

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  if (posts.length === 0) return null;

  return (
    // Outer wrapper — transparent, no background color set here
    <div className="max-w-full w-full relative flex flex-col gap-1 pb-7">
      <div className="relative max-w-full w-full rounded-[10px]">
        {/*
          Glow wrapper — this is the key technique from the cannabis DataTable.

          The before: pseudo-element is positioned to start at top-[44px],
          which is exactly the height of the header row (py-3 + 11px text ≈ 44px).
          This means:
            - Headers sit ABOVE the white glow box → they appear to "float" with
              no background behind them, just the page bg showing through.
            - The white bg + shadow wraps ONLY the tbody rows.
            - No background leaks below the last row because before:bottom-0
              stays flush with the wrapper's bottom edge.

          If your header row height changes, adjust before:top-[Npx] to match.
        */}
        <div
          className={cn(
            'relative z-0 w-full overflow-visible',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-[44px] before:bottom-0",
            'before:rounded-3xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
        >
          <div className="relative z-10 w-full overflow-x-auto overflow-y-visible">
            <table className="w-full border-collapse text-sm text-left min-w-[700px]">
              {/*
                Headers — intentionally NO background class.
                The thead tr is transparent so the page background shows through,
                making headers look like they float above the card body below.
              */}
              <thead>
                <tr>
                  <th className="py-3 px-4 whitespace-nowrap">
                    <button
                      onClick={toggleSort}
                      className="flex items-center gap-1 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase hover:text-[#555] transition-colors"
                    >
                      Post Content
                      {sortDir === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )}
                    </button>
                  </th>

                  {(['Platforms', 'AI Generated', 'Status', 'Tags', 'Publish Date'] as const).map(
                    (label) => (
                      <th
                        key={label}
                        className="py-3 px-4 whitespace-nowrap text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase"
                      >
                        {label}
                      </th>
                    ),
                  )}
                </tr>
              </thead>

              {/*
                Body rows.
                - bg-white on every row (sits on top of the before: white bg)
                - Dashed bottom border between rows, skipped on the last row
                - First row: top corners rounded to hug the glow box
                - Last row: bottom corners rounded to hug the glow box
              */}
              <tbody className="relative w-full">
                {sorted.map((post, i) => {
                  const platforms = Object.keys(post.platformContent);
                  const hasPlatformContent = post.hasAiContent;
                  const isFirst = i === 0;
                  const isLast = i === sorted.length - 1;

                  return (
                    <tr
                      key={post.postId}
                      onClick={() => navigate(`/a/arche_social_media/compose/${post.postId}`)}
                      className={cn(
                        'transition bg-background cursor-pointer hover:bg-muted/50',
                        !isLast && 'border-b border-dashed border-b-[rgba(0,0,0,0.08)]',
                        isFirst &&
                          '[&>td:first-child]:rounded-tl-3xl [&>td:last-child]:rounded-tr-3xl',
                        isLast &&
                          '[&>td:first-child]:rounded-bl-3xl [&>td:last-child]:rounded-br-3xl',
                      )}
                    >
                      {/* Post Content */}
                      <td className="px-4 py-4 align-middle">
                        {post.title && (
                          <p className="text-[13px] font-[500] text-[rgba(0,0,0,0.)] mb-0.5 truncate max-w-[280px]">
                            {post.title}
                          </p>
                        )}
                        <p className="text-[11px] font-[500] text-[#1A1A1A] truncate max-w-[280px]">
                          {post.originalContent || (
                            <span className="text-[#9A9A9A] italic font-[400]">No content</span>
                          )}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <p className="text-[11px] text-[#9A9A9A] font-[400]">
                            {new Date(post.createdAt).toLocaleString()}
                          </p>
                          {post.mediaFileIds.length > 0 && (
                            <span className="flex items-center gap-0.5 text-[12px] text-[#9A9A9A]">
                              <Paperclip className="h-3 w-3" />
                              {post.mediaFileIds.length}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Platforms */}
                      <td className="px-4 py-4 align-middle">
                        {platforms.length > 0 ? (
                          <div className="flex flex-row gap-1 text-[#444]">
                            {platforms.map((p) => (
                              <span key={p}>{PLATFORM_LABELS[p] ?? p}</span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-[14px] text-[#9A9A9A]">—</span>
                        )}
                      </td>

                      {/* AI Generated */}
                      <td className="px-4 py-4 align-middle">
                        <Pill
                          className={
                            hasPlatformContent
                              ? 'bg-[#F0EEFF] text-[#6B4EFF]'
                              : 'bg-[#F0F0F0] text-[#444444]'
                          }
                        >
                          {hasPlatformContent ? 'AI Crafted' : 'Manual'}
                        </Pill>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 align-middle">
                        <div className="flex items-center gap-1.5">
                          <Pill className={STATUS_CLASSES[post.status]}>
                            {STATUS_LABELS[post.status]}
                          </Pill>
                          {post.failureCount > 0 && (
                            <span
                              className="flex items-center gap-0.5 text-[12px] text-red-500"
                              title={`${post.failureCount} publish attempt(s) failed`}
                            >
                              <AlertTriangle className="h-3 w-3" />
                              {post.failureCount}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Tags */}
                      <td className="px-4 py-4 align-middle">
                        {post.tags.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {post.tags.slice(0, 2).map((tag) => (
                              <Pill
                                key={tag}
                                className="bg-[#F0F0F0] text-[#666666] text-[11px] px-2 py-0"
                              >
                                {tag}
                              </Pill>
                            ))}
                            {post.tags.length > 2 && (
                              <span className="text-[12px] text-[#9A9A9A] self-center">
                                +{post.tags.length - 2}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-[14px] text-[#9A9A9A]">—</span>
                        )}
                      </td>

                      {/* Publish Date */}
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        {post.status === 'scheduled' || post.status === 'published' ? (
                          <>
                            <p className="text-[14px] font-[500] text-[#1A1A1A]">
                              {new Date(post.publishAt).toLocaleDateString()}
                            </p>
                            <p className="text-[12px] text-[#9A9A9A] font-[400]">
                              {new Date(post.publishAt).toLocaleTimeString()}
                            </p>
                          </>
                        ) : (
                          <span className="text-[14px] text-[#9A9A9A]">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
